import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import {
  CaretDownOutlined,
  KeyOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import { api } from "../api";
import { useAuth } from "../auth";
import type { JobSummary, ScraperSchema } from "../types";
import { ScraperDot } from "./ScraperTag";

const POLL_MS = 4000;

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrapers, setScrapers] = useState<ScraperSchema[]>([]);
  const [recent, setRecent] = useState<JobSummary[]>([]);
  const [counts, setCounts] = useState<{ live: number; total: number }>({ live: 0, total: 0 });

  useEffect(() => {
    api.scrapers().then(({ scrapers }) => setScrapers(scrapers)).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function tick() {
      try {
        const res = await api.listTasks({ page: 1, per_page: 5 });
        if (cancelled) return;
        setRecent(res.items);
        const live = res.items.filter(
          (j) => j.status === "queued" || j.status === "running",
        ).length;
        setCounts({ live, total: res.total });
      } catch {
        /* ignore */
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    }
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const scraperLabel = useMemo(() => {
    const m: Record<string, string> = {};
    scrapers.forEach((s) => (m[s.name] = s.label));
    return m;
  }, [scrapers]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const userInitials = (user?.email ?? "?")
    .split("@")[0]!
    .split(/[^a-zA-Z]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "U";

  return (
    <div className="crl-app">
      <aside className="crl-sidebar">
        <Dropdown
          menu={{
            items: [
              { key: "logout", label: "Logout", onClick: handleLogout },
            ],
          }}
          trigger={["click"]}
          placement="bottomLeft"
        >
          <div className="crl-workspace">
            <div className="crl-workspace-logo">C</div>
            <div className="crl-workspace-name">Crawly</div>
            <CaretDownOutlined className="crl-workspace-chev" style={{ fontSize: 10 }} />
          </div>
        </Dropdown>

        <div className="crl-nav-group">Workspace</div>
        <SidebarLink to="/scrapers" icon={<ThunderboltOutlined />}>
          New run
        </SidebarLink>
        <SidebarLink
          to="/output"
          icon={<UnorderedListOutlined />}
          badge={
            counts.live > 0 ? (
              <span className="crl-badge live">{counts.live} live</span>
            ) : counts.total > 0 ? (
              <span className="crl-badge gray">{counts.total}</span>
            ) : null
          }
        >
          Tasks
        </SidebarLink>
        <SidebarLink to="/api" icon={<KeyOutlined />}>
          API & Keys
        </SidebarLink>

        <div className="crl-nav-group">
          Scrapers
          <span
            className="crl-add"
            onClick={() => navigate("/scrapers")}
            title="New run"
          >
            <PlusOutlined style={{ fontSize: 11 }} />
          </span>
        </div>
        {scrapers.map((s) => (
          <NavLink
            key={s.name}
            to={`/scrapers?s=${s.name}`}
            className={({ isActive }) =>
              `crl-nav-item${isActive ? " active" : ""}`
            }
          >
            <ScraperDot name={s.name} />
            <span className="crl-lbl">{s.label}</span>
          </NavLink>
        ))}

        {recent.length > 0 && (
          <>
            <div className="crl-nav-group">Recent</div>
            {recent.map((j) => (
              <NavLink
                key={j.id}
                to={`/output/${j.id}`}
                className={({ isActive }) =>
                  `crl-nav-item thin${isActive ? " active" : ""}`
                }
              >
                <span className={`crl-status-dot ${statusDotClass(j.status)}`} />
                <span className="crl-lbl">
                  #{j.seq ?? j.id.slice(0, 6)} · {scraperLabel[j.scraper] ?? j.scraper}
                </span>
              </NavLink>
            ))}
          </>
        )}

        <div className="crl-nav-footer">
          <Dropdown
            menu={{ items: [{ key: "logout", label: "Logout", onClick: handleLogout }] }}
            trigger={["click"]}
            placement="topLeft"
          >
            <div className="crl-nav-user">
              <div className="crl-avatar">{userInitials}</div>
              <div className="crl-who">
                <div className="crl-email">{user?.email}</div>
                <div className="crl-role">Workspace admin</div>
              </div>
            </div>
          </Dropdown>
        </div>
      </aside>

      <div className="crl-main">
        <Outlet />
      </div>
    </div>
  );
}

function SidebarLink({
  to,
  icon,
  children,
  badge,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={to === "/output"}
      className={({ isActive }) => `crl-nav-item${isActive ? " active" : ""}`}
    >
      {icon}
      <span className="crl-lbl">{children}</span>
      {badge}
    </NavLink>
  );
}

function statusDotClass(s: string): string {
  if (s === "done") return "done";
  if (s === "running" || s === "queued") return "running";
  if (s === "failed") return "failed";
  return "";
}
